import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { useCurator } from "../useCurator.js";
import { trpc } from "../trpc.js";

/**
 * «Кому отправим письмо сегодня?» — список людей, о ком куратор уже
 * написал пользователю (letters.list). Отсюда юзер выбирает, кому
 * отправить ответное письмо. До первого письма показываем honest empty.
 *
 * Раньше тут был мок из 4-х женщин (Аня/Марина/Лиза/Катя) — введение
 * в заблуждение: юзер видел имена до того, как его с кем-то познакомили.
 */
export function Curator(): JSX.Element {
  const navigate = useNavigate();
  const c = useCurator();
  const lettersQ = trpc.letters.list.useQuery(undefined, { staleTime: 30_000 });

  const letters = lettersQ.data?.letters ?? [];
  const recipients = letters
    .filter((l) => l.subject)
    .map((l, i) => ({
      id: l.id,
      num: `№ ${String(letters.length - i).padStart(2, "0")}`,
      dative: dativeFromName(l.subject?.firstName ?? "собеседница"),
      kicker: cityLabel(l.subject?.city ?? null, l.subject?.age ?? null),
    }));

  const [picked, setPicked] = useState<string | null>(null);
  const [text, setText] = useState("");
  const recipient = recipients.find((r) => r.id === picked) ?? null;
  const canSend = recipient != null && text.trim().length >= 6;

  const onSend = (): void => {
    if (!canSend) return;
    navigate(`/sent?next=${encodeURIComponent("/home")}&ms=8000`);
  };

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right={`${c.footerLabel} · письмо`} />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[28px]">
        <button
          type="button"
          onClick={() => {
            navigate("/home");
          }}
          className="self-start editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← в редакцию
        </button>

        <div className="mt-[22px] pr-[18px]">
          <CuratorBubble signature={c.shortSig}>
            {recipients.length > 0 ? (
              <>
                «Я здесь. Напишем сегодня <em className="italic text-velvet">кому-то одному</em> —
                кому?»
              </>
            ) : (
              <>«Пока подбираю. Первое письмо придёт в ближайшие дни — ждите.»</>
            )}
          </CuratorBubble>
        </div>

        <h2 className="mt-[34px] font-display font-light leading-[1.02] tracking-[-0.022em] text-ink text-[40px]">
          {recipients.length > 0 ? (
            <>
              Кому отправим
              <br />
              письмо <em className="italic text-velvet">сегодня</em>?
            </>
          ) : (
            <>
              Первое письмо
              <br />
              <em className="italic text-velvet">ещё в работе.</em>
            </>
          )}
        </h2>

        {recipients.length > 0 ? (
          <>
            <div className="mt-[22px] editorial-meta text-ink/55">из вашей редакции</div>

            <ul className="mt-[10px]">
              {recipients.map((r, i) => {
                const isPicked = picked === r.id;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setPicked(r.id);
                      }}
                      aria-pressed={isPicked}
                      className={`flex w-full items-baseline justify-between gap-[14px] text-left py-[14px] transition-colors ${
                        i === 0 ? "border-t" : ""
                      } border-b border-ink/12`}
                    >
                      <div className="flex items-baseline gap-[14px]">
                        <span
                          className={`editorial-meta w-[38px] flex-none ${
                            isPicked ? "text-accent" : "text-ink/45"
                          }`}
                        >
                          {r.num}
                        </span>
                        <span
                          className={`font-display font-light text-[22px] tracking-[-0.015em] ${
                            isPicked ? "italic text-velvet" : "text-ink"
                          }`}
                        >
                          {r.dative}
                        </span>
                      </div>
                      <span className="editorial-meta text-ink/50">{r.kicker}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {recipient && (
              <div className="mt-[24px] border-l-2 border-accent/60 pl-[14px]">
                <div className="flex items-baseline justify-between">
                  <div className="editorial-kicker text-accent">письмо {recipient.dative}</div>
                  <span className="editorial-meta text-ink/40">{text.length} / 600</span>
                </div>
                <textarea
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value.slice(0, 600));
                  }}
                  rows={5}
                  autoFocus
                  placeholder="пишите свободно — Марк передаст как есть…"
                  className="mt-[8px] block w-full resize-none bg-transparent font-display italic font-light text-[17px] leading-[1.5] text-ink outline-none placeholder:text-ink/30 placeholder:not-italic"
                />
              </div>
            )}
          </>
        ) : (
          <p className="mt-[24px] font-display italic font-light text-[17px] leading-[1.5] text-ink/65">
            {c.firstName} читает ваши ответы и сейчас перебирает карточки — ищет человека, которому
            не надо объяснять ваши вечера. Письмо придёт, как только редактор закончит.
          </p>
        )}

        <p className="mt-auto pt-[22px] font-display italic font-light text-[13px] leading-[1.45] text-ink/50">
          Обычно занимает до вечера. Можно закрыть и вернуться — уведомим.
        </p>
      </div>

      {recipients.length > 0 && (
        <div className="px-[22px] pb-[24px] pt-[16px] space-y-[12px]">
          <PrimaryButton disabled={!canSend} onClick={onSend}>
            Передать {c.footerLabel}
          </PrimaryButton>
        </div>
      )}
    </Screen>
  );
}

function dativeFromName(name: string): string {
  // Грубая эвристика: женские имена на -а/-я → -е, иначе оставляем как есть.
  if (name.endsWith("а")) return name.slice(0, -1) + "е";
  if (name.endsWith("я")) return name.slice(0, -1) + "е";
  return name;
}

function cityLabel(city: string | null, age: number | null): string {
  if (city && age) return `${city} · ${String(age)}`;
  if (city) return city;
  if (age) return String(age);
  return "";
}
